import React from 'react';
import { TagBadge } from './DietaryFilter';
import PricingTiers from './PricingTiers';

/* ---------- Item Card ---------- */
const ItemCard = ({ item, testId }) => {
  const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;

  return (
    <article
      className="h-full rounded-2xl border border-[#E8B945]/40 bg-gradient-to-br from-[#5C1815] via-[#4A1412] to-[#3A1210] shadow-[0_0_16px_rgba(232,185,69,0.08)] hover:shadow-[0_0_22px_rgba(232,185,69,0.2)] hover:border-[#E8B945]/80 transition-all duration-300 px-6 py-5 sm:px-7 sm:py-6"
      data-testid={testId}
    >
      {/* Title row with price */}
      <div className="flex justify-between items-start gap-3 mb-3">
        <h3
          className="text-[#F5D06E] font-black uppercase tracking-wider text-lg sm:text-xl leading-tight"
          style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
        >
          {item.name}
          {item.portion && (
            <span className="text-[#F5E6C9]/80 font-normal text-sm normal-case tracking-normal ml-2">
              {item.portion}
            </span>
          )}
        </h3>
        {item.price !== undefined && (
          <span
            className="text-[#F5D06E] font-black text-2xl whitespace-nowrap"
            style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
          >
            ${item.price}
          </span>
        )}
      </div>

      {item.tags && item.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {item.tags.map((t) => (
            <TagBadge key={t} code={t} />
          ))}
        </div>
      )}

      {item.description && (
        <p className="text-[#F5E6C9] uppercase tracking-wide text-xs sm:text-sm leading-relaxed font-medium">
          {item.description}
        </p>
      )}

      {hasVariants && (
        <div className="mt-4 space-y-2">
          {item.variants.map((v, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 rounded-lg bg-[#2B130F]/50 pl-4 pr-4 py-2.5 border-l-4 border-l-[#F5D06E]"
            >
              <span className="text-[#F5E6C9] uppercase tracking-wide text-xs sm:text-sm font-semibold">
                {v.name}
              </span>
              <span
                className="text-[#F5D06E] font-bold text-base sm:text-lg"
                style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
              >
                ${v.price}
              </span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
};

/* ---------- Items Grid (3 cols on desktop) ---------- */
const ItemsGrid = ({ items, sectionId, groupId }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
    {items.map((item, idx) => (
      <ItemCard
        key={idx}
        item={item}
        testId={
          groupId
            ? `menu-item-${sectionId}-${groupId}-${idx}`
            : `menu-item-${sectionId}-${idx}`
        }
      />
    ))}
  </div>
);

/* ---------- List Mode Subgroup (Sides / Drinks compact rows) ---------- */
const ListSubgroup = ({ subgroup, testId }) => (
  <div
    className="h-full rounded-2xl border border-[#E8B945]/50 bg-gradient-to-br from-[#5C1815] via-[#4A1412] to-[#3A1210] shadow-[0_0_16px_rgba(232,185,69,0.08)] px-6 py-6 sm:px-7"
    data-testid={testId}
  >
    <div className="flex items-center justify-between gap-4 mb-4 border-b border-[#E8B945]/40 pb-3">
      <div>
        <h3
          className="text-[#F5D06E] font-black uppercase tracking-wider text-base sm:text-lg"
          style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
        >
          {subgroup.title}
        </h3>
        {subgroup.subtitle && (
          <p className="text-[#F5E6C9]/70 italic text-xs mt-1">{subgroup.subtitle}</p>
        )}
      </div>
      {subgroup.groupPrice !== undefined && (
        <span
          className="text-[#F5D06E] font-black text-2xl"
          style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
        >
          ${subgroup.groupPrice}
        </span>
      )}
    </div>
    <ul className="space-y-2">
      {subgroup.items.map((item, idx) => (
        <li
          key={idx}
          className="flex items-center justify-between gap-3 text-[#F5E6C9] text-sm sm:text-base"
        >
          <span className="flex items-center gap-3 flex-1 min-w-0">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F5D06E] shrink-0" aria-hidden="true" />
            <span className="uppercase tracking-wide truncate">{item.name}</span>
            {item.tags && item.tags.length > 0 && (
              <span className="flex flex-wrap gap-1.5">
                {item.tags.map((t) => (
                  <TagBadge key={t} code={t} />
                ))}
              </span>
            )}
          </span>
          {item.price !== undefined && (
            <span
              className="text-[#F5D06E] font-bold whitespace-nowrap"
              style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
            >
              ${item.price}
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

/* ---------- Section headings ---------- */
const SectionTitle = ({ title, subtitle, footnote }) => (
  <div className="text-center mb-8 px-4">
    <h2
      className="text-[#F5D06E] font-black tracking-[0.02em] uppercase mb-2 drop-shadow-[0_2px_10px_rgba(232,185,69,0.35)]"
      style={{
        fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif",
        fontSize: 'clamp(2rem, 6vw, 3.5rem)',
        lineHeight: 1.1
      }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        className="text-[#E8D5B7]/80 italic tracking-[0.12em] uppercase text-sm sm:text-base"
        style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
      >
        {subtitle}
      </p>
    )}
    {footnote && (
      <p className="text-[#E8D5B7]/60 italic text-xs sm:text-sm mt-2">{footnote}</p>
    )}
    <div className="mt-4 h-px w-48 mx-auto bg-gradient-to-r from-transparent via-[#E8B945] to-transparent" />
  </div>
);

const SubgroupHeading = ({ title, subtitle, footnote }) => (
  <div className="flex items-start gap-3 mt-12 mb-6 px-1">
    <span className="w-1.5 h-10 bg-[#F5D06E] rounded-full flex-shrink-0 mt-1" />
    <div>
      <div
        className="inline-block text-[#F5D06E] font-black uppercase tracking-wider text-lg sm:text-xl px-3 py-1 border-2 border-[#E8B945] rounded-lg shadow-[0_0_10px_rgba(232,185,69,0.25)]"
        style={{ fontFamily: "'Akzidenz-Grotesk Next', 'Inter', sans-serif" }}
      >
        {title}
      </div>
      {subtitle && (
        <p className="text-[#E8D5B7]/80 italic text-xs sm:text-sm mt-2 tracking-wider uppercase">
          {subtitle}
        </p>
      )}
      {footnote && (
        <p className="text-[#E8D5B7]/60 italic text-xs mt-1">{footnote}</p>
      )}
    </div>
  </div>
);

/* ---------- MenuSection ---------- */
const MenuSection = ({ section }) => {
  const hasSubgroups = Array.isArray(section.subgroups) && section.subgroups.length > 0;
  const hasTopItems = Array.isArray(section.items) && section.items.length > 0;

  const totalItems =
    (hasSubgroups ? section.subgroups.reduce((sum, sg) => sum + (sg.items || []).length, 0) : 0) +
    (hasTopItems ? section.items.length : 0);

  return (
    <section
      className="py-14 sm:py-16 px-4 sm:px-6"
      data-testid={`menu-section-${section.id}`}
    >
      <SectionTitle
        title={section.title}
        subtitle={section.subtitle}
        footnote={section.footnote}
      />

      {section.pricingTiers && <PricingTiers tiers={section.pricingTiers} />}

      <div className="max-w-7xl mx-auto">
        {totalItems === 0 ? (
          <p className="text-center text-[#E8D5B7]/60 italic py-10">
            No dishes match the selected filters.
          </p>
        ) : (
          <>
            {hasTopItems && (
              <ItemsGrid items={section.items} sectionId={section.id} />
            )}

            {/* List-mode subgroups render side-by-side in a grid */}
            {hasSubgroups && section.subgroups.every((sg) => sg.listMode) && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {section.subgroups.map(
                  (sg) =>
                    (sg.items || []).length > 0 && (
                      <div
                        key={sg.id}
                        data-subgroup={`${section.id}-${sg.id}`}
                        className="scroll-mt-24"
                      >
                        <ListSubgroup
                          subgroup={sg}
                          testId={`menu-subgroup-${section.id}-${sg.id}`}
                        />
                      </div>
                    )
                )}
              </div>
            )}

            {/* Card-mode subgroups render stacked with their own heading */}
            {hasSubgroups &&
              section.subgroups.some((sg) => !sg.listMode) &&
              section.subgroups.map(
                (sg) =>
                  !sg.listMode &&
                  (sg.items || []).length > 0 && (
                    <div
                      key={sg.id}
                      data-subgroup={`${section.id}-${sg.id}`}
                      className="scroll-mt-24"
                    >
                      <SubgroupHeading
                        title={sg.title}
                        subtitle={sg.subtitle}
                        footnote={sg.footnote}
                      />
                      <ItemsGrid
                        items={sg.items}
                        sectionId={section.id}
                        groupId={sg.id}
                      />
                    </div>
                  )
              )}
          </>
        )}
      </div>
    </section>
  );
};

export default MenuSection;
